import { Check } from "lucide-react";

import { CurrencyFlag } from "@/components/cost/CurrencyFlag";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/hint";
import { useReportingCurrency } from "@/hooks/useReportingCurrency";
import type { ReportingCurrency } from "@/lib/constants/userPreferences";
import { cn } from "@/lib/utils";

/**
 * The reporting currency, declared once and changeable from anywhere.
 *
 * It sits beside Settings rather than in a page header because it is a property
 * of the whole session, not of one screen. Its previous home was the Cost
 * Analysis header, which made it invisible on every other page that shows
 * money.
 *
 * **Flags mark currencies, never amounts.** Amounts are formatted by `Intl`,
 * which already renders `$1,234.50` for USD and `CA$1,234.50` for CAD, so a
 * flag on every figure would repeat at every row what this states once. The
 * one other place a flag appears is beside a *billed* currency that differs
 * from this one (the service view's "Billed in"), where the reader is
 * comparing two currencies and the flags are how they compare them.
 *
 * Being permanently on screen is the point: `$` alone does not say whether a
 * total is American or Canadian dollars, and a number read in the wrong
 * currency is wrong by an exchange rate while looking entirely plausible.
 */
export function SidebarCurrency({
  collapsed,
  compact = false,
}: {
  collapsed: boolean;
  /**
   * Share a row with Settings instead of occupying one.
   *
   * A full-width row for three letters gave a preference the same weight as a
   * destination, in the one part of the rail where the eye is looking for
   * destinations. Compact keeps it adjacent to Settings -- which is what it is,
   * a setting -- while staying permanently readable, which a menu item would
   * not be.
   */
  compact?: boolean;
}) {
  const { currency, setCurrency, supported } = useReportingCurrency();

  // One supported currency is not a choice, and a control that cannot change
  // anything invites a click that does nothing. It still *states* the currency,
  // because that is the half of the job that always matters.
  const changeable = supported.length > 1;

  const face = (
    <>
      <CurrencyFlag currency={currency} />
      {!collapsed && (
        <span
          className={cn(
            "text-left font-medium tabular-nums",
            !compact && "flex-1",
          )}
        >
          {currency}
        </span>
      )}
    </>
  );

  const shape = cn(
    "group flex items-center rounded-md text-sm font-medium text-sidebar-muted",
    collapsed
      ? "w-full justify-center py-2.5"
      : compact
        ? // Sits beside Settings rather than under it. The hairline border is
          // what separates two adjacent controls on one row -- without it the
          // flag reads as a trailing icon belonging to Settings.
          "shrink-0 gap-1.5 border border-sidebar-border/60 px-2 py-2"
        : "w-full gap-3 px-3 py-2.5",
  );

  // Folded, the face is a flag alone, so the currency it stands for is the
  // hint beside it.
  const hint = collapsed ? `Reporting currency: ${currency}` : undefined;

  if (!changeable) {
    return (
      <Hint content={hint} side="right">
        <div className={shape}>{face}</div>
      </Hint>
    );
  }

  return (
    <DropdownMenu>
      <Hint content={hint} side="right">
        <DropdownMenuTrigger
          className={cn(
            shape,
            "transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
          // The visible face is a flag and a three-letter code, which reads as an
          // ornament rather than a control. The label says what it is and that it
          // can be changed.
          aria-label={`Reporting currency: ${currency}. Change reporting currency.`}
        >
          {face}
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent side="right" align="end" className="min-w-[11rem]">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Show all costs in
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {supported.map((code) => (
          <DropdownMenuItem
            key={code}
            onSelect={() => void setCurrency(code as ReportingCurrency)}
            className="gap-2.5"
          >
            <CurrencyFlag currency={code} />
            <span className="flex-1 tabular-nums">{code}</span>
            {code === currency ? (
              <Check className="h-4 w-4 shrink-0" aria-hidden />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
